// =============== Data Models (shared between frontend and backend) ===============

/** 单章节版本总上限（自动保存 50 + 其他操作 50） */
export const MAX_CHAPTER_VERSIONS = 100;
/** 自动保存版本独立配额：仅删除最老的自动保存版本 */
export const MAX_AUTO_SAVE_VERSIONS = 50;
/** 手动保存 / AI 修改等其他操作共用配额 */
export const MAX_MANUAL_VERSIONS = 50;

export interface User {
  id: string;
  username: string;
  password_hash: string;
  created_at: string;
}

export interface Book {
  id: string;
  user_id: string;
  title: string;
  description: string;
  cover: string;
  created_at: string;
  updated_at: string;
  word_count: number;
  is_active: boolean;
}

export interface Volume {
  id: string;
  user_id: string;
  book_id: string;
  title: string;
  description: string;
  order_index: number;
  /**
   * 建作品时自动创建的"默认卷"。
   *
   * 分卷此前是硬前置（新作品必须先建卷才能加章节），而不少网文并不需要分卷。
   * 现在建作品即自动建一个默认卷，让"新建章节"永远可用；此标记用于：
   * 只有它一个卷时章节列表**平铺显示**（不出现卷层级），且**禁止删除**
   * （删卷会连带删除卷下所有章节）。历史作品该值为 false，行为不变。
   */
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * 章节写作状态。
 * 长篇需要一眼看出"哪些章还没改完"，此前只能把状态手写在标题里。
 */
export type ChapterStatus = 'draft' | 'revising' | 'done';

export const CHAPTER_STATUS_LABELS: Record<ChapterStatus, string> = {
  draft: '草稿',
  revising: '修订中',
  done: '已完成',
};

export const CHAPTER_STATUSES: ChapterStatus[] = ['draft', 'revising', 'done'];

/** 每章字数目标的默认档位（字）；作品可通过章节的 target_words 覆盖 */
export const DEFAULT_CHAPTER_TARGET_WORDS = 3000;

export interface Chapter {
  id: string;
  user_id: string;
  volume_id: string;
  book_id: string;
  title: string;
  summary: string;
  content: string;
  word_count: number;
  chapter_number: number;
  /** 写作状态（默认 draft） */
  status: ChapterStatus;
  /** 本章字数目标；0 表示未单独设置，回落到作品/全局默认 */
  target_words: number;
  created_at: string;
  updated_at: string;
}

export interface Setting {
  id: string;
  user_id: string;
  book_id: string;
  title: string;
  content: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface Faction {
  id: string;
  user_id: string;
  book_id: string;
  name: string;
  parent_faction_id: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Character {
  id: string;
  user_id: string;
  book_id: string;
  name: string;
  age: string | null;
  faction_id: string | null;
  strength: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Item {
  id: string;
  user_id: string;
  book_id: string;
  name: string;
  type: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserPreference {
  id: string;
  user_id: string;
  preference_name: string;
  preference_value: string | null;
  created_at: string;
  updated_at: string;
}

export interface AIPromptTemplate {
  id: string;
  user_id: string;
  name: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface AIChatSession {
  id: string;
  user_id: string;
  session_uuid: string;
  book_id: string;
  mode: string;
  model_name: string | null;
  provider: string;
  title: string | null;
  messages: string;
  activity_snapshot: string | null;
  chapter_id: string | null;
  chapter_title: string | null;
  created_at: string;
  updated_at: string;
  /** 会话级用量与费用（列表接口附加计算）：累计人民币费用，模型无价格配置时为 null */
  costCny?: number | null;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

export interface ChapterVersion {
  id: string;
  user_id: string;
  chapter_id: string;
  book_id: string;
  title: string;
  summary: string;
  content: string;
  word_count: number;
  created_at: string;
  source: 'manual_save' | 'auto_save' | 'leave_chapter' | 'app_close' | 'ai_modify';
}

export type RightPanelType = 'none' | 'ai_assistant' | 'version_history';

/** 线索类型：伏笔 / 支线 / 悬念 / 承诺 */
export type PlotThreadKind = 'foreshadow' | 'subplot' | 'mystery' | 'promise';
/** 线索状态：已埋未收 / 已回收 / 已废弃 */
export type PlotThreadStatus = 'open' | 'resolved' | 'dropped';

export const PLOT_THREAD_KIND_LABELS: Record<PlotThreadKind, string> = {
  foreshadow: '伏笔',
  subplot: '支线',
  mystery: '悬念',
  promise: '承诺',
};

export const PLOT_THREAD_STATUS_LABELS: Record<PlotThreadStatus, string> = {
  open: '未回收',
  resolved: '已回收',
  dropped: '已废弃',
};

export const PLOT_THREAD_KINDS: PlotThreadKind[] = ['foreshadow', 'subplot', 'mystery', 'promise'];

/**
 * 伏笔 / 线索条目。
 *
 * 长篇写作的核心痛点：埋了伏笔要记得回收。此前没有结构支持，
 * 只能在章节摘要里手写备注，跨几十万字必然遗漏。
 */
export interface PlotThread {
  id: string;
  user_id: string;
  book_id: string;
  kind: PlotThreadKind;
  title: string;
  detail: string;
  status: PlotThreadStatus;
  planted_chapter_id: string | null;
  resolved_chapter_id: string | null;
  /**
   * 标记时选中的正文摘录（从编辑器右键「添加伏笔」标记时写入）。
   *
   * 手工在伏笔页登记时为空 —— 它是"这条线索对应正文哪句话"的可读凭据，
   * 隔几十章回看时不至于对着一句线索标题发愣。
   */
  quote: string;
  /**
   * 摘录在该章 TipTap 文档中的起止位置（标记时刻的快照）。
   *
   * 正文编辑后可能失准，故定位实现先按偏移选中，落空再用 `quote`
   * 文本搜索兜底（见 Web 端 `findQuoteRange`）。
   */
  quote_start: number | null;
  quote_end: number | null;
  created_at: string;
  updated_at: string;
  /** 以下为列表接口附加的章节编号/标题（免去前端再查一次） */
  planted_chapter_number?: number | null;
  planted_chapter_title?: string | null;
  resolved_chapter_number?: number | null;
  resolved_chapter_title?: string | null;
}

/** 人物关系常用类型（自由文本，UI 提供这些作为快捷选项） */
export const COMMON_RELATION_TYPES = [
  '师徒', '亲属', '好友', '敌对', '同门', '上下级', '恋人', '仇敌', '结义', '主仆',
] as const;

/**
 * 人物关系（有向边，`bidirectional` 表示双向）。
 *
 * 此前人物只有静态字段（年龄/所属势力/战力），无法表达"谁和谁是什么关系"，
 * 长篇里师徒/仇敌/亲属的梳理只能靠脑子记。
 */
export interface CharacterRelation {
  id: string;
  user_id: string;
  book_id: string;
  from_character_id: string;
  to_character_id: string;
  relation_type: string;
  bidirectional: boolean;
  note: string;
  created_at: string;
  updated_at: string;
  /** 列表接口附加的人物名（免去前端再查一次） */
  from_character_name?: string | null;
  to_character_name?: string | null;
}

/** 人物出场章节（由全书检索反查得到） */
export interface CharacterAppearance {
  chapter_id: string;
  chapter_number: number;
  chapter_title: string;
  /** 该章正文中的出现次数 */
  count: number;
  snippet: string;
  snippetMatchStart: number;
  snippetMatchLength: number;
}

export interface ChapterSearchHit {
  id: string;
  volume_id: string;
  title: string;
  /** 所属卷标题（结果列表直接展示，免去前端再查一次） */
  volume_title: string;
  chapter_number: number;
  summary: string;
  word_count: number;
  /** 该章命中总次数（正文 + 标题 + 摘要） */
  matchCount: number;
  /** 正文内首个命中的上下文片段（无正文命中时为空串） */
  snippet: string;
  /** 片段中首个命中的起始偏移（相对 snippet，-1 表示不高亮） */
  snippetMatchStart: number;
  /** 命中长度（用于高亮） */
  snippetMatchLength: number;
  /** 标题或摘要命中时为 true（前端可标注"标题命中"） */
  matchedTitle: boolean;
  matchedSummary: boolean;
}
